import type { ColumnDef } from '@tanstack/vue-table'
import { DateModel } from 'utility-class'
import { CHART_STACKING_TYPE, CHART_TYPE, currencyFormatter, YAXIS_POSITION } from 'xangle-charts'

import type {
  LlamaChartInfo,
  LlamaTvlChartDataInfo, MetricInfo,
  ParentProtocolTvlInfo,
  ProtocolListInfo,
  ProtocolTvlInfo
} from '~/api/repository'
import type { defiCategoryType, metricTableDataType } from '~/types/defi.type'
import { MetricTableHeader } from '~/types/model/analytics/analytics-chart.model'

export class DefiModel {
  readonly index: number
  readonly projectId: string
  readonly officialLink: string
  readonly logo: string
  readonly name: string
  private readonly _category: string
  readonly chainList: string[]
  private readonly _tvl: string
  readonly tvlDailyChange: string
  readonly tvlWeeklyChange: string
  readonly tvlMonthlyChange: string
  private readonly _marketCapPerTvl: string
  private readonly _childDefiList?: Array<ProtocolTvlInfo>

  constructor(data: ParentProtocolTvlInfo | ProtocolTvlInfo, index: number) {
    this.index = index
    this.projectId = data.PID
    this.officialLink = data.OFFIURL
    this.logo = data.LOGO
    this.name = data.NM
    this._category = data.CTGR
    this.chainList = data.CHNLIST
    this._tvl = data.TVL
    this.tvlDailyChange = data.TVL1D
    this.tvlWeeklyChange = data.TVL7D
    this.tvlMonthlyChange = data.TVL1M
    this._marketCapPerTvl = data.MCAPTVL
    if ('CHDRPRTCLIST' in data) {
      this._childDefiList = data.CHDRPRTCLIST
    }
  }

  get tvl() {
    return currencyFormatter({
      number: this._tvl,
      contraction: true,
      unit: '$',
      decimalPoint: 2,
      showDecimalZero: false,
      showZero: true,
    })
  }

  get category() {
    return this._category as defiCategoryType
  }

  get marketCapPerTvl() {
    return currencyFormatter({
      number: this._marketCapPerTvl,
      contraction: true,
      decimalPoint: 2,
      showDecimalZero: false,
      showZero: false,
    })
  }

  get childDefiList() {
    if (!this._childDefiList || !this._childDefiList.length) {
      return undefined
    }
    return this._childDefiList.map((defi, index) => new DefiModel(defi, index + 1))
  }

  static convertSortField(sortField: string) {
    switch (sortField) {
      case 'tvl':
        return 'TVL'
      case 'tvlDailyChange':
        return 'D1'
      case 'tvlWeeklyChange':
        return 'D7'
      case 'tvlMonthlyChange':
        return 'M1'
      case 'marketCapPerTvl':
        return 'MCAPTVL'
      default:
        return sortField
    }
  }
}

export class DefiListModel {
  readonly totalCount: number
  private readonly _defiList: Array<ParentProtocolTvlInfo>
  readonly lastCount: number

  constructor(data: ProtocolListInfo, lastCount: number) {
    this.totalCount = Number(data.TCNT)
    this._defiList = data.PRTCLIST
    this.lastCount = lastCount
  }

  get formattedTotalCount() {
    return currencyFormatter({
      number: this.totalCount,
      contraction: false,
      showZero: true,
    })
  }

  get defiList() {
    return this._defiList.map((defi, index) => new DefiModel(defi, this.lastCount + index + 1))
  }

  get source() {
    return 'Powered by Defillama'
  }
}

export class DefiChartModel {
  readonly tvl: string
  readonly tvlChange: string
  readonly price: string
  readonly priceChange: string
  private readonly _chartList: Array<LlamaTvlChartDataInfo>
  private readonly _metricInfo: MetricInfo

  constructor(data: LlamaChartInfo) {
    this.tvl = data.TVL
    this.tvlChange = data.TVL24H
    this.price = data.PRICE
    this.priceChange = data.PRICE24H
    this._chartList = data.CHARTLIST
    this._metricInfo = data.MTC
  }

  get formattedTvl() {
    return currencyFormatter({
      number: this.tvl,
      contraction: true,
      decimalPoint: 2,
      showDecimalZero: true,
      showZero: true,
    })
  }

  get formattedPrice() {
    return currencyFormatter({
      number: this.price,
      contraction: true,
      decimalPoint: 2,
      showDecimalZero: true,
      showZero: true,
    })
  }

  get title() {
    return 'TVL'
  }

  get source() {
    return 'Powered by Defillama'
  }

  get chartList() {
    return [ {
      name: 'tvl',
      yAxis: YAXIS_POSITION.RIGHT,
      type: CHART_TYPE.LINE,
      stacking: CHART_STACKING_TYPE.NONE,
      decimalPoint: 2,
      data: this._chartList.map(data => {
        const timestamp = new DateModel(data.date ?? 0)
        return {
          day: timestamp.date.toISO() ?? '',
          value: data.tvl ?? 0
        }
      }) ?? []
    } ]
  }

  get metricInfo() {
    const headers: Array<ColumnDef<metricTableDataType>> = [
      {
        header: MetricTableHeader['7DAY_PER'],
        accessorKey: 'week',
      },
      {
        header: MetricTableHeader['1MONTH_PER'],
        accessorKey: 'month',
      },
      {
        header: MetricTableHeader['6MONTH_PER'],
        accessorKey: 'half',
      }
    ]
    const items: Array<metricTableDataType> = [ this._metricInfo.DATA.reduce((acc, cur) => {
      return {
        ...acc,
        [cur.TFRMTP.toLowerCase()]: cur.VAL,
      }
    }, {} as metricTableDataType) ]
    return { headers, items }
  }
}
